import { observer } from "mobx-react";
import PricingStore from ".";
import { PricingPayload, PricingState } from "./Types";
import { AxiosResponse, AxiosError } from "axios";
import BaseReaction, { BaseReactionProps } from "../../BaseReaction";
import { Api } from "../../utils/Api";

interface State {
  pricingState: PricingState;
  quotationParams: PricingStore["quotationParams"];
}

export interface Props extends BaseReactionProps<PricingStore> {
  store: PricingStore;
  params: any;
}

class PricingReactions extends BaseReaction<PricingStore, State, Props> {
  queryString = () => {
    const params = {
      course_slug: this.props.params.slug,
      ...this.props.store.quotationParams,
    };

    const query = Object.keys(params)
      .filter((key) => params[key] !== undefined && params[key] !== "")
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`,
      )
      .join("&");

    return query ? `?${query}` : "";
  };

  tester = (): State => ({
    pricingState: this.props.store.pricingState,
    quotationParams: this.props.store.quotationParams,
  });

  // The request currently in flight, if any.
  //
  // A course page used to issue this exact quotation four times. Two things stack up
  // to cause it. `tester` returns a fresh object literal and the reaction compares
  // with mobx's default (reference) comparer, so it re-runs on *every* write to
  // either observable -- including setQuotationParams({}) when the params were
  // already {}. And the effect below re-requested on seeing "loading" without
  // checking whether it was already asking for the same thing. Two legitimate
  // loading() calls (DynamicLayoutRoute on mount, CoursePreview once the course
  // resolves) then each got doubled.
  //
  // Comparing the built URL covers both: a re-fire with identical params is a no-op,
  // while a genuine change -- a new currency, say -- produces a different URL and
  // goes through.
  pendingUrl: string | null = null;

  effect = ({ pricingState }: State) => {
    switch (pricingState.kind) {
      case "loading":
        const url = `/api/merchant/quotations.json${this.queryString()}`;

        if (url === this.pendingUrl) {
          break;
        }

        this.pendingUrl = url;
        const client = new Api({});

        client
          .get<PricingPayload>(url)
          .then((response: AxiosResponse<PricingPayload>) => {
            this.pendingUrl = null;
            this.props.store.ready(response.data);
          })
          .catch((error: AxiosError<PricingPayload>) => {
            this.pendingUrl = null;
            this.props.store.ready(error.response.data);
          });
        break;

      case "waiting":
      case "ready":
        break;
    }
  };
}

export default observer(PricingReactions);
